import React, { useState, useEffect, useMemo } from 'react';
import { 
  Form, Input, DatePicker, Select, Button, Card, Row, Col, Typography, 
  message, Space, Divider, Table, Tag, Tooltip, Modal, Alert, Checkbox
} from 'antd';
import { 
  ArrowLeftOutlined, CalendarOutlined, FileTextOutlined, 
  RightOutlined, DeleteOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import apiClient from '../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface AssetForCampaign {
  id: number;
  asset_code: string;
  name: string;
  maintenance_type_id: number | null;
  maintenance_type_name: string | null;
  cycle_months: number | null;
  next_due_date: string | null;
  last_work_date?: string | null;
  location_name?: string;
  assigned_to_employee_name?: string;
  is_overdue?: boolean;
  is_parent?: boolean;
  parent_asset_id?: number | null;
}

const CreateMaintenanceCampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // State cho dữ liệu mapping
  const [companies, setCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState<any[]>([]);
  
  // State cho filtered data
  const [filteredLocations, setFilteredLocations] = useState<any[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<any[]>([]);
  
  // State cho selection
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<AssetForCampaign[]>([]);
  
  // State cho tìm kiếm
  const [assets, setAssets] = useState<AssetForCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // State cho phân quyền
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Helper functions
  const getCurrentUser = (): any => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  };

  // Tạo tên đợt tác nghiệp tự động
  const generateCampaignName = useMemo(() => {
    if (!selectedLocation || !form.getFieldValue('date_range')) {
      return '';
    }
    
    const location = filteredLocations.find(l => l.id === selectedLocation);
    const dateRange = form.getFieldValue('date_range');
    
    if (!location || !dateRange || !dateRange[0]) {
      return '';
    }
    
    const month = dayjs(dateRange[0]).format('MM');
    const year = dayjs(dateRange[0]).format('YYYY');
    
    return `TNTS định kỳ - ${location.name} - ${month}/${year}`;
  }, [selectedLocation, filteredLocations, form]);

  // Cập nhật tên đợt tác nghiệp khi thay đổi địa điểm hoặc thời gian
  useEffect(() => {
    if (generateCampaignName) {
      form.setFieldsValue({ name: generateCampaignName });
    }
  }, [generateCampaignName, form]);

  // Fetch mapping data
  const fetchMappingData = async () => {
    try {
      const user = getCurrentUser();
      const isSuperAdminUser = user.role === 'super_admin';
      
      let companiesRes, locationsRes, employeesRes;
      
      if (isSuperAdminUser) {
        // Super admin load tất cả
        [companiesRes, locationsRes, employeesRes] = await Promise.all([
          apiClient.getCompanies(),
          apiClient.getLocations(),
          apiClient.getEmployees(),
        ]);
      } else {
        // Company admin chỉ load dữ liệu của công ty mình
        companiesRes = await apiClient.getCompanies();
        locationsRes = await apiClient.getLocations();
        employeesRes = await apiClient.getEmployees();
        
        // Lọc theo company_id
        if (user.company_id) {
          companiesRes = (companiesRes as any[]).filter(c => c.id === user.company_id);
          locationsRes = (locationsRes as any[]).filter(l => l.company_id === user.company_id);
          employeesRes = (employeesRes as any[]).filter(e => e.company_id === user.company_id);
        }
      }
      
      const maintenanceTypesRes = await apiClient.getMaintenanceTypes();
      
      console.log('🔍 Debug fetchMappingData:');
      console.log('   - companiesRes:', companiesRes);
      console.log('   - locationsRes:', locationsRes);
      console.log('   - employeesRes:', employeesRes);
      console.log('   - maintenanceTypesRes:', maintenanceTypesRes);
      
      setCompanies(companiesRes as any[]);
      setLocations(locationsRes as any[]);
      setEmployees(employeesRes as any[]);
      setMaintenanceTypes(maintenanceTypesRes as any[]);
      setFilteredLocations([]);
      setFilteredEmployees([]);
    } catch (error) {
      console.error('Lỗi khi tải dữ liệu mapping:', error);
      message.error('Không thể tải dữ liệu mapping');
    }
  };

  useEffect(() => {
    fetchMappingData();
  }, []);

  // Sau khi load xong dữ liệu, tự động chọn công ty cho company admin
  useEffect(() => {
    if (locations.length > 0 && employees.length > 0 && currentUser && !isSuperAdmin && currentUser.company_id) {
      handleCompanyChange(currentUser.company_id);
    }
  }, [locations, employees, currentUser, isSuperAdmin]);

  // Kiểm tra phân quyền
  useEffect(() => {
    const user = getCurrentUser();
    const isSuperAdminUser = user.role === 'super_admin';
    setIsSuperAdmin(isSuperAdminUser);
    setCurrentUser(user);

    // Nếu không phải super admin, tự động chọn công ty
    if (!isSuperAdminUser && user.company_id) {
      setSelectedCompany(user.company_id);
      // Không gọi handleCompanyChange ở đây vì locations và employees chưa load xong
    }
  }, []);

  const handleCompanyChange = (companyId: number) => {
    setSelectedCompany(companyId);
    setSelectedLocation(null);
    
    // Lọc địa điểm và nhân viên theo công ty
    const companyLocations = locations.filter(location => location.company_id === companyId);
    const companyEmployees = employees.filter(employee => employee.company_id === companyId);
    
    console.log('🔍 Debug handleCompanyChange:');
    console.log('   - selectedCompany:', companyId);
    console.log('   - total locations:', locations.length);
    console.log('   - total employees:', employees.length);
    console.log('   - companyLocations:', companyLocations);
    console.log('   - companyEmployees:', companyEmployees);
    
    setFilteredLocations(companyLocations);
    setFilteredEmployees(companyEmployees);
    
    // Reset form fields
    form.setFieldsValue({
      location_id: undefined,
      conducted_by_employee_id: undefined
    });

    // Nếu không phải super admin, tự động set company_id trong form
    if (!isSuperAdmin && currentUser?.company_id) {
      form.setFieldsValue({ company_id: currentUser.company_id });
    }
  };

  const handleLocationChange = (locationId: number) => {
    setSelectedLocation(locationId);
    // Tự động tìm kiếm khi chọn địa điểm
    if (locationId && selectedCompany) {
      handleSearchAssets(locationId);
    }
  };

  const handleDateRangeChange = () => {
    // Tự động tìm kiếm khi thay đổi thời gian thực hiện
    if (selectedLocation && selectedCompany) {
      handleSearchAssets();
    }
  };

  const handleSearchAssets = async (locationId?: number) => {
    const targetLocationId = locationId || selectedLocation;
    
    if (!targetLocationId || !selectedCompany) {
      message.error('Vui lòng chọn công ty và địa điểm');
      return;
    }

    const formValues = form.getFieldsValue();
    const { date_range } = formValues;

    if (!date_range || !date_range[0] || !date_range[1]) {
      message.error('Vui lòng chọn thời gian thực hiện');
      return;
    }

    setLoading(true);
    try {
      const startDate = dayjs(date_range[0]).format('YYYY-MM-DD');
      const endDate = dayjs(date_range[1]).format('YYYY-MM-DD');

      const result = await apiClient.getAssetsByLocationAndDateRange(targetLocationId, startDate, endDate) as any;
      setAssets(result.assets || []);
      
      const totalCount = result.total || 0;
      
      if (totalCount > 0) {
        const startDateFormatted = dayjs(startDate).format('DD/MM/YYYY');
        const endDateFormatted = dayjs(endDate).format('DD/MM/YYYY');
        message.success(`Tìm thấy ${totalCount} tài sản cần tác nghiệp từ ${startDateFormatted} đến ${endDateFormatted}`);
      } else {
        message.info('Không tìm thấy tài sản nào cần tác nghiệp trong khoảng thời gian này');
      }
    } catch (error) {
      console.error('Lỗi khi tìm kiếm tài sản:', error);
      message.error('Có lỗi xảy ra khi tìm kiếm tài sản');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAsset = (asset: AssetForCampaign) => {
    if (!selectedAssets.find(a => a.id === asset.id && a.maintenance_type_id === asset.maintenance_type_id)) {
      setSelectedAssets([...selectedAssets, asset]);
    }
  };

  const handleRemoveAsset = (assetId: number, maintenanceTypeId?: number | null) => {
    setSelectedAssets(selectedAssets.filter(a => 
      !(a.id === assetId && a.maintenance_type_id === maintenanceTypeId)
    ));
  };

  // Chọn tất cả tác nghiệp của một tài sản
  const handleSelectAllAssetOperations = (assetId: number) => {
    const children = getChildren(assetId);
    const allChildrenSelected = children.every(child => 
      selectedAssets.some(a => a.id === child.id && a.maintenance_type_id === child.maintenance_type_id)
    );

    if (allChildrenSelected) {
      // Bỏ chọn tất cả
      setSelectedAssets(selectedAssets.filter(a => a.id !== assetId));
    } else {
      // Chọn tất cả
      const newSelectedAssets = selectedAssets.filter(a => a.id !== assetId);
      children.forEach(child => {
        if (!newSelectedAssets.some(a => a.id === child.id && a.maintenance_type_id === child.maintenance_type_id)) {
          newSelectedAssets.push(child);
        }
      });
      setSelectedAssets(newSelectedAssets);
    }
  };

  // Kiểm tra xem tất cả tác nghiệp của một tài sản đã được chọn chưa
  const isAllAssetOperationsSelected = (assetId: number) => {
    const children = getChildren(assetId);
    return children.length > 0 && children.every(child => 
      selectedAssets.some(a => a.id === child.id && a.maintenance_type_id === child.maintenance_type_id)
    );
  };

  // Chọn tất cả tác nghiệp trong danh sách
  const handleSelectAllOperations = (checked: boolean) => {
    if (checked) {
      // Chọn tất cả tác nghiệp có thể chọn (không phải parent)
      const selectableAssets = assets.filter(asset => !asset.is_parent);
      setSelectedAssets(selectableAssets);
    } else {
      // Bỏ chọn tất cả
      setSelectedAssets([]);
    }
  };

  // Kiểm tra xem tất cả tác nghiệp đã được chọn chưa
  const isAllOperationsSelected = () => {
    const selectableAssets = assets.filter(asset => !asset.is_parent);
    return selectableAssets.length > 0 && selectableAssets.every(asset => 
      selectedAssets.some(a => a.id === asset.id && a.maintenance_type_id === asset.maintenance_type_id)
    );
  };

  // Kiểm tra xem có tác nghiệp nào được chọn không (cho indeterminate state)
  const hasAnyOperationSelected = () => {
    const selectableAssets = assets.filter(asset => !asset.is_parent);
    return selectableAssets.some(asset => 
      selectedAssets.some(a => a.id === asset.id && a.maintenance_type_id === asset.maintenance_type_id)
    );
  };

  const handleSubmit = async (values: any) => {
    if (selectedAssets.length === 0) {
      message.error('Vui lòng chọn ít nhất một tài sản');
      return;
    }

    setSubmitting(true);
    try {
      console.log('🔍 Form values nhận được:', values);
      console.log('   - conducted_by_employee_id:', values.conducted_by_employee_id);
      
      const campaignData = {
        ...values,
        start_date: dayjs(values.date_range[0]).format('YYYY-MM-DD'),
        end_date: dayjs(values.date_range[1]).format('YYYY-MM-DD'),
        asset_ids: selectedAssets.map(asset => asset.id),
        // Gửi trực tiếp conducted_by_employee_id
        conducted_by_employee_id: values.conducted_by_employee_id
      };

      console.log('📤 Campaign data sẽ gửi:', campaignData);

      await apiClient.createMaintenanceCampaign(campaignData);
      message.success('Tạo đợt tác nghiệp định kỳ thành công');
      navigate('/maintenance-campaigns');
    } catch (error: any) {
      console.error('❌ Lỗi khi tạo đợt tác nghiệp:', error);
      console.error('   - Response data:', error?.response?.data);
      message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tạo đợt tác nghiệp');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/maintenance-campaigns');
  };

  // Tạo dữ liệu cho expandable table
  const expandableData = useMemo(() => {
    return assets.filter(asset => asset.is_parent || !asset.parent_asset_id);
  }, [assets]);

  // Lấy children cho expandable
  const getChildren = (parentId: number) => {
    return assets.filter(asset => asset.parent_asset_id === parentId);
  };

  // Columns cho expandable table
  const expandableColumns = [
    {
      title: (
        <Checkbox
          checked={isAllOperationsSelected()}
          indeterminate={hasAnyOperationSelected() && !isAllOperationsSelected()}
          onChange={(e) => handleSelectAllOperations(e.target.checked)}
        />
      ),
      key: 'select',
      width: 60,
      render: (_: any, record: AssetForCampaign) => {
        if (record.is_parent) {
          const children = getChildren(record.id);
          if (children.length === 0) {
            return null; // Không hiển thị checkbox nếu không có children
          }
          return (
            <Checkbox
              checked={isAllAssetOperationsSelected(record.id)}
              indeterminate={children.some(child => 
                selectedAssets.some(a => a.id === child.id && a.maintenance_type_id === child.maintenance_type_id)
              ) && !isAllAssetOperationsSelected(record.id)}
              onChange={() => handleSelectAllAssetOperations(record.id)}
            />
          );
        }
        return (
          <Checkbox
            checked={selectedAssets.some(a => a.id === record.id && a.maintenance_type_id === record.maintenance_type_id)}
            onChange={(e) => {
              if (e.target.checked) {
                handleSelectAsset(record);
              } else {
                handleRemoveAsset(record.id, record.maintenance_type_id);
              }
            }}
          />
        );
      },
    },
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      width: 150,
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name: string, record: AssetForCampaign) => {
        if (record.is_parent) {
          return <span style={{ fontWeight: 'bold' }}>{name}</span>;
        }
        // Subrow: hiển thị tên tài sản với indent
        return (
          <div style={{ paddingLeft: '20px', color: '#666' }}>
            {name || '-'}
          </div>
        );
      },
    },
    {
      title: 'Loại bảo dưỡng',
      dataIndex: 'maintenance_type_name',
      key: 'maintenance_type_name',
      width: 180,
      render: (maintenance_type_name: string | null, record: AssetForCampaign) => {
        if (record.is_parent) {
          return <span style={{ fontStyle: 'italic', color: '#666' }}>Nhiều loại tác nghiệp</span>;
        }
        return maintenance_type_name || '-';
      },
    },
    {
      title: 'Chu kỳ',
      dataIndex: 'cycle_months',
      key: 'cycle_months',
      width: 100,
      render: (cycle_months: number | null, record: AssetForCampaign) => {
        if (record.is_parent) {
          return '-';
        }
        return cycle_months ? `${cycle_months} tháng` : '-';
      },
    },
    {
      title: 'Ngày bảo dưỡng tiếp theo',
      dataIndex: 'next_due_date',
      key: 'next_due_date',
      width: 200,
      render: (next_due_date: string | null, record: AssetForCampaign) => {
        if (record.is_parent) {
          return '-';
        }
        if (!next_due_date) return '-';
        
        return (
          <span style={{ color: record.is_overdue ? '#ff4d4f' : 'inherit' }}>
            {dayjs(next_due_date).format('DD/MM/YYYY')}
            {record.is_overdue && <Tag color="red" style={{ marginLeft: 8 }}>Quá hạn</Tag>}
          </span>
        );
      },
    },
  ];

  // Cấu hình expandable
  const expandableConfig = {
    expandedRowRender: (record: AssetForCampaign) => {
      const children = getChildren(record.id);
      return (
        <Table
          columns={[
            {
              title: 'Chọn',
              key: 'select',
              width: 60,
              render: (_: any, childRecord: AssetForCampaign) => (
                <Checkbox
                  checked={selectedAssets.some(a => a.id === childRecord.id && a.maintenance_type_id === childRecord.maintenance_type_id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleSelectAsset(childRecord);
                    } else {
                      handleRemoveAsset(childRecord.id, childRecord.maintenance_type_id);
                    }
                  }}
                />
              ),
            },
            {
              title: 'Mã tài sản',
              dataIndex: 'asset_code',
              key: 'asset_code',
              width: 150,
              render: () => null, // Không hiển thị gì cho subrow
            },
            {
              title: 'Tên tài sản',
              dataIndex: 'name',
              key: 'name',
              width: 200,
              render: (name: string) => name || '-',
            },
            {
              title: 'Loại bảo dưỡng',
              dataIndex: 'maintenance_type_name',
              key: 'maintenance_type_name',
              width: 180,
            },
            {
              title: 'Chu kỳ',
              dataIndex: 'cycle_months',
              key: 'cycle_months',
              width: 100,
              render: (cycle_months: number | null) => cycle_months ? `${cycle_months} tháng` : '-',
            },
            {
              title: 'Ngày bảo dưỡng tiếp theo',
              dataIndex: 'next_due_date',
              key: 'next_due_date',
              width: 200,
              render: (next_due_date: string | null, childRecord: AssetForCampaign) => {
                if (!next_due_date) return '-';
                return (
                  <span style={{ color: childRecord.is_overdue ? '#ff4d4f' : 'inherit' }}>
                    {dayjs(next_due_date).format('DD/MM/YYYY')}
                    {childRecord.is_overdue && <Tag color="red" style={{ marginLeft: 8 }}>Quá hạn</Tag>}
                  </span>
                );
              },
            },
          ]}
          dataSource={children}
          rowKey={(childRecord) => `${childRecord.id}-${childRecord.maintenance_type_id}`}
          pagination={false}
          size="small"
          showHeader={false} // Ẩn header của subtable
        />
      );
    },
    rowExpandable: (record: AssetForCampaign) => Boolean(record.is_parent && getChildren(record.id).length > 0),
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Button 
          type="link" 
          icon={<ArrowLeftOutlined />} 
          onClick={handleCancel}
          style={{ padding: 0, marginBottom: '8px' }}
        >
          Quay lại
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          Tạo đợt tác nghiệp định kỳ
        </Title>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          date_range: [dayjs().startOf('month'), dayjs().endOf('month')]
        }}
      >
        {/* Main Content - 2 columns */}
        <Row gutter={24} style={{ marginBottom: '24px' }}>
          {/* Left Column: Thông tin đợt tác nghiệp */}
          <Col span={16}>
            <Card title="Thông tin đợt tác nghiệp">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Tên đợt tác nghiệp định kỳ"
                    rules={[{ required: true, message: 'Vui lòng nhập tên đợt tác nghiệp' }]}
                  >
                    <Input 
                      placeholder="Tên sẽ được tạo tự động"
                      disabled={true}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="date_range"
                    label="Thời gian thực hiện"
                    rules={[{ required: true, message: 'Vui lòng chọn thời gian thực hiện' }]}
                  >
                    <RangePicker
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      placeholder={['Ngày bắt đầu', 'Ngày kết thúc']}
                      onChange={handleDateRangeChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="company_id"
                    label="Công ty"
                    rules={[{ required: true, message: 'Vui lòng chọn công ty' }]}
                  >
                    <Select
                      placeholder={isSuperAdmin ? "Chọn công ty" : currentUser?.company_id ? companies.find(c => c.id === currentUser.company_id)?.name || "Công ty hiện tại" : "Chọn công ty"}
                      options={isSuperAdmin 
                        ? companies.map(company => ({ value: company.id, label: company.name }))
                        : currentUser?.company_id 
                          ? companies.filter(c => c.id === currentUser.company_id).map(company => ({ value: company.id, label: company.name }))
                          : []
                      }
                      onChange={handleCompanyChange}
                      disabled={!isSuperAdmin}
                      style={!isSuperAdmin ? { backgroundColor: '#f5f5f5' } : {}}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="location_id"
                    label="Địa điểm"
                    rules={[{ required: true, message: 'Vui lòng chọn địa điểm' }]}
                  >
                    <Select
                      placeholder={selectedCompany ? "Chọn địa điểm" : "Vui lòng chọn công ty trước"}
                      disabled={!selectedCompany}
                      options={filteredLocations.map(location => ({ value: location.id, label: location.name }))}
                      onChange={handleLocationChange}
                      notFoundContent={selectedCompany && filteredLocations.length === 0 ? "Không có địa điểm nào cho công ty này" : "Không tìm thấy"}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="conducted_by_employee_id"
                    label="Người phụ trách"
                    rules={[{ required: true, message: 'Vui lòng chọn người phụ trách' }]}
                  >
                    <Select
                      placeholder={selectedCompany ? (filteredEmployees.length === 0 ? "Không có nhân viên nào" : "Chọn người phụ trách") : "Vui lòng chọn công ty trước"}
                      disabled={!selectedCompany || filteredEmployees.length === 0}
                      options={filteredEmployees.map(employee => ({ 
                        value: employee.id, 
                        label: employee.full_name || employee.name || `Nhân viên ${employee.id}` 
                      }))}
                      notFoundContent={selectedCompany && filteredEmployees.length === 0 ? "Không có nhân viên nào cho công ty này" : "Không tìm thấy"}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Right Column: Thống kê */}
          <Col span={8}>
            <Card title="Thống kê">
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                  {new Set(selectedAssets.map(a => a.id)).size}
                </div>
                <div style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
                  Tài sản được chọn
                </div>
                <div style={{ textAlign: 'left', fontSize: '14px', color: '#666' }}>
                  <div style={{ marginBottom: '8px' }}>
                    Địa điểm: {selectedLocation ? filteredLocations.find(l => l.id === selectedLocation)?.name || '—' : '—'}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    Tổng tài sản: {new Set(assets.filter(a => !a.is_parent).map(a => a.id)).size}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    Tổng tác nghiệp: {assets.filter(a => !a.is_parent).length}
                  </div>
                  <div>
                    Tác nghiệp đã chọn: {selectedAssets.length}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Bottom Section: Danh sách tài sản */}
        <Card 
          title={`Danh sách tài sản cần tác nghiệp ${assets.length > 0 ? `(${new Set(assets.filter(a => !a.is_parent).map(a => a.id)).size} tài sản)` : ''}`}
          style={{ marginBottom: '24px' }}
        >
          {assets.length > 0 ? (
            <Table
              dataSource={expandableData}
              columns={expandableColumns}
              rowKey="id"
              size="small"
              expandable={expandableConfig}
              pagination={false}
              loading={loading}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              <Text>Vui lòng chọn địa điểm để xem danh sách tài sản cần tác nghiệp</Text>
            </div>
          )}
        </Card>

        {/* Actions */}
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => form.submit()}
            loading={submitting}
            disabled={selectedAssets.length === 0}
            size="large"
          >
            Tạo đợt tác nghiệp định kỳ
          </Button>
        </div>
      </Form>

      <style>{`
        /* Đảm bảo subtable có cùng width với table chính */
        .ant-table-expanded-row .ant-table {
          margin: -16px -16px -16px 0;
        }
        /* Ẩn border của subtable */
        .ant-table-expanded-row .ant-table-container {
          border: none;
        }
        /* Ẩn header của subtable */
        .ant-table-expanded-row .ant-table-thead {
          display: none;
        }
        /* Tạo hiệu ứng subrow với background khác biệt */
        .ant-table-expanded-row .ant-table-tbody > tr {
          background-color: #fafafa;
        }
        .ant-table-expanded-row .ant-table-tbody > tr:hover {
          background-color: #f5f5f5;
        }
        /* Thêm padding nhẹ cho subrow */
        .ant-table-expanded-row .ant-table-tbody > tr > td {
          padding: 8px 16px;
        }
      `}</style>
    </div>
  );
};

export default CreateMaintenanceCampaignPage; 